/**
 * GsplatWeather - Procedural weather effect using gaussian splats
 *
 * Ported from PlayCanvas 2.18 official gsplat-weather script.
 * https://github.com/playcanvas/engine/blob/v2.18.0/scripts/esm/gsplat/gsplat-weather.mjs
 *
 * Creates a camera-relative volume of particles that appear fixed in world space,
 * falling and drifting infinitely. Each particle occupies a cell in a 3D grid;
 * its world position is derived from a hash of the world-space cell coordinate.
 *
 * Unlike the upstream implementation, this is a plain class (not a pc.Script)
 * so it integrates cleanly with the StorySplat viewer's lifecycle. Call update(dt)
 * on every frame; call destroy() to tear down.
 */
import * as pc from 'playcanvas';

const readGLSL = /* glsl */`
    uniform float uTime;
    uniform float uCellSize;
    uniform vec3 uGridHalf;
    uniform vec3 uBaseCell;
    uniform vec3 uCameraPos;
    uniform float uSpeed;
    uniform float uDrift;
    uniform float uOpacity;
    uniform vec3 uColor;
    uniform vec2 uParticleSize;
    uniform float uElongate;
    // Scene wind: xy = drift in cells (wrapped), w = 1 while drifting; rotation (w, x, y, z) tilts streaks
    uniform vec4 uWindDrift;
    uniform vec4 uWindRot;

    vec3 weatherLocalPos;
    vec3 weatherWC;
    vec4 sd;

    float weatherHash(vec3 p) {
        return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
    }

    vec3 getCenter() {
        sd = loadData();

        float dx = floor(sd.r * 255.0 + 0.5) - uGridHalf.x;
        float dz = floor(sd.b * 255.0 + 0.5) - uGridHalf.z;
        float dy = floor(sd.g * 255.0 + 0.5);

        vec3 worldCell = uBaseCell + vec3(dx, dy - uGridHalf.y, dz);
        weatherWC = worldCell;

        vec3 col = vec3(worldCell.x, 0.0, worldCell.z);

        float fx = weatherHash(col + vec3(1.0, 0.0, 0.0));
        float fz = weatherHash(col + vec3(0.0, 0.0, 3.0));

        fx += (weatherHash(worldCell + vec3(4.0, 0.0, 0.0)) - 0.5) * 0.4;
        fz += (weatherHash(worldCell + vec3(0.0, 0.0, 5.0)) - 0.5) * 0.4;

        float spd = mix(0.3, 0.8, weatherHash(col)) * uSpeed;

        float gridYf = uGridHalf.y * 2.0;
        float colY = (dy + weatherHash(worldCell + vec3(0.0, 2.0, 0.0))) / gridYf;
        colY = fract(colY - uTime * spd / gridYf);

        float phase = weatherHash(col + vec3(7.0, 0.0, 0.0)) * 6.28318;
        fx += sin(uTime * 0.8 + phase) * uDrift;
        fz += cos(uTime * 0.6 + phase) * uDrift;

        float lx = dx + fx;
        float lz = dz + fz;
        if (uWindDrift.w > 0.5) {
            // drift with the wind, wrapping inside the camera-relative grid (its edges are faded out)
            lx = lx + uWindDrift.x;
            lz = lz + uWindDrift.y;
            lx = lx - floor((lx + uGridHalf.x) / (2.0 * uGridHalf.x)) * 2.0 * uGridHalf.x;
            lz = lz - floor((lz + uGridHalf.z) / (2.0 * uGridHalf.z)) * 2.0 * uGridHalf.z;
        }
        weatherLocalPos = vec3(
            lx * uCellSize,
            (-uGridHalf.y + colY * gridYf) * uCellSize,
            lz * uCellSize
        );
        return weatherLocalPos;
    }

    vec4 getColor() {
        vec3 camOffset = uCameraPos - uBaseCell * uCellSize;
        float dist = length(weatherLocalPos - camOffset);
        float maxDist = min(uGridHalf.x, uGridHalf.z) * uCellSize * 0.9;
        float fade = 1.0 - smoothstep(maxDist * 0.6, maxDist, dist);
        float alpha = mix(0.5, 0.9, weatherHash(weatherWC + 20.0)) * fade * uOpacity;
        return vec4(uColor, alpha);
    }

    vec3 getScale() {
        float size = mix(uParticleSize.x, uParticleSize.y, weatherHash(weatherWC + 10.0));
        return vec3(size, size * uElongate, size);
    }

    vec4 getRotation() { return uWindDrift.w > 0.5 ? uWindRot : vec4(0.0, 0.0, 0.0, 1.0); }
`;

const readWGSL = /* wgsl */`
    uniform uTime: f32;
    uniform uCellSize: f32;
    uniform uGridHalf: vec3f;
    uniform uBaseCell: vec3f;
    uniform uCameraPos: vec3f;
    uniform uSpeed: f32;
    uniform uDrift: f32;
    uniform uOpacity: f32;
    uniform uColor: vec3f;
    uniform uParticleSize: vec2f;
    uniform uElongate: f32;
    uniform uWindDrift: vec4f;
    uniform uWindRot: vec4f;

    var<private> weatherLocalPos: vec3f;
    var<private> weatherWC: vec3f;
    var<private> sd: vec4f;

    fn weatherHash(p: vec3f) -> f32 {
        return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
    }

    fn getCenter() -> vec3f {
        sd = loadData();

        let dx = floor(sd.r * 255.0 + 0.5) - uniform.uGridHalf.x;
        let dz = floor(sd.b * 255.0 + 0.5) - uniform.uGridHalf.z;
        let dy = floor(sd.g * 255.0 + 0.5);

        let worldCell = uniform.uBaseCell + vec3f(dx, dy - uniform.uGridHalf.y, dz);
        weatherWC = worldCell;

        let col = vec3f(worldCell.x, 0.0, worldCell.z);

        var fx = weatherHash(col + vec3f(1.0, 0.0, 0.0));
        var fz = weatherHash(col + vec3f(0.0, 0.0, 3.0));

        fx = fx + (weatherHash(worldCell + vec3f(4.0, 0.0, 0.0)) - 0.5) * 0.4;
        fz = fz + (weatherHash(worldCell + vec3f(0.0, 0.0, 5.0)) - 0.5) * 0.4;

        let spd = mix(0.3, 0.8, weatherHash(col)) * uniform.uSpeed;

        let gridYf = uniform.uGridHalf.y * 2.0;
        var colY = (dy + weatherHash(worldCell + vec3f(0.0, 2.0, 0.0))) / gridYf;
        colY = fract(colY - uniform.uTime * spd / gridYf);

        let phase = weatherHash(col + vec3f(7.0, 0.0, 0.0)) * 6.28318;
        fx = fx + sin(uniform.uTime * 0.8 + phase) * uniform.uDrift;
        fz = fz + cos(uniform.uTime * 0.6 + phase) * uniform.uDrift;

        var lx = dx + fx;
        var lz = dz + fz;
        if (uniform.uWindDrift.w > 0.5) {
            lx = lx + uniform.uWindDrift.x;
            lz = lz + uniform.uWindDrift.y;
            lx = lx - floor((lx + uniform.uGridHalf.x) / (2.0 * uniform.uGridHalf.x)) * 2.0 * uniform.uGridHalf.x;
            lz = lz - floor((lz + uniform.uGridHalf.z) / (2.0 * uniform.uGridHalf.z)) * 2.0 * uniform.uGridHalf.z;
        }
        weatherLocalPos = vec3f(
            lx * uniform.uCellSize,
            (-uniform.uGridHalf.y + colY * gridYf) * uniform.uCellSize,
            lz * uniform.uCellSize
        );
        return weatherLocalPos;
    }

    fn getColor() -> vec4f {
        let camOffset = uniform.uCameraPos - uniform.uBaseCell * uniform.uCellSize;
        let dist = length(weatherLocalPos - camOffset);
        let maxDist = min(uniform.uGridHalf.x, uniform.uGridHalf.z) * uniform.uCellSize * 0.9;
        let fade = 1.0 - smoothstep(maxDist * 0.6, maxDist, dist);
        let alpha = mix(0.5, 0.9, weatherHash(weatherWC + 20.0)) * fade * uniform.uOpacity;
        return vec4f(uniform.uColor, alpha);
    }

    fn getScale() -> vec3f {
        let size = mix(uniform.uParticleSize.x, uniform.uParticleSize.y, weatherHash(weatherWC + 10.0));
        return vec3f(size, size * uniform.uElongate, size);
    }

    fn getRotation() -> vec4f { return select(vec4f(0.0, 0.0, 0.0, 1.0), uniform.uWindRot, uniform.uWindDrift.w > 0.5); }
`;

export interface GsplatWeatherOptions {
    extents?: { x: number; y: number; z: number };
    density?: number;
    speed?: number;
    drift?: number;
    opacity?: number;
    color?: [number, number, number];
    particleMinSize?: number;
    particleMaxSize?: number;
    elongate?: number;
    followEntity?: pc.Entity | null;
}

// Soft upper bound on total particle count. Protects low-VRAM devices (mobile
// integrated GPU) from an OOM when users combine large extents with high density.
// At this cap the data texture is ~4MB and centers Float32Array is ~12MB,
// comfortably safe on typical mobile budgets.
const MAX_WEATHER_PARTICLES = 1_000_000;

export class GsplatWeather {
    private app: pc.AppBase;
    public entity: pc.Entity;
    public followEntity: pc.Entity | null;

    extents = new pc.Vec3(12, 12, 12);
    density = 2;
    speed = 1.0;
    drift = 0.15;
    opacity = 0.8;
    color: [number, number, number] = [1, 1, 1];
    particleMinSize = 0.006;
    particleMaxSize = 0.012;
    elongate = 1.0;

    private _container: any = null;
    private _format: any = null;
    private _time = 0;
    private _baseCellArray: [number, number, number] = [0, 0, 0];
    private _camPosArray: [number, number, number] = [0, 0, 0];
    private _gridHalfArray: [number, number, number] = [0, 0, 0];
    private _particleSizeArray: [number, number] = [0, 0];
    // Effective half-cell counts after the MAX_WEATHER_PARTICLES cap is
    // applied in _buildContainer. The shader must decode cell coordinates
    // with these capped dimensions, otherwise particles render with the wrong
    // grid size and shift outside the AABB.
    private _effectiveHalfCells: [number, number, number] = [0, 0, 0];
    // Dirty flag for non-time uniforms (speed/drift/opacity/color/size/elongate).
    // Set by applyOptions(); cleared after upload in update(). Saves ~10
    // setParameter calls and a material.update() per frame in steady state —
    // measurable mobile CPU win when several gsplat materials coexist.
    private _scalarsDirty = true;
    // Scene wind (effects/wind): horizontal drift in world units / s, the drift so far (cells,
    // wrapped to the grid) and the streak tilt (w, x, y, z as getRotation reads it).
    private _windX = 0;
    private _windZ = 0;
    private _windDrift = new Float32Array(4);
    private _windRot = new Float32Array([0, 0, 0, 1]);
    private _windPushed = false;

    constructor(app: pc.AppBase, options: GsplatWeatherOptions = {}) {
        this.app = app;
        this.entity = new pc.Entity('GsplatWeather');
        this.followEntity = options.followEntity ?? null;
        this.applyOptions(options);
        this._buildFormat();
        this._buildContainer();
    }

    applyOptions(options: GsplatWeatherOptions) {
        if (options.extents) this.extents.set(options.extents.x, options.extents.y, options.extents.z);
        if (options.density !== undefined) this.density = options.density;
        if (options.speed !== undefined) this.speed = options.speed;
        if (options.drift !== undefined) this.drift = options.drift;
        if (options.opacity !== undefined) this.opacity = options.opacity;
        if (options.color) this.color = [options.color[0], options.color[1], options.color[2]];
        if (options.particleMinSize !== undefined) this.particleMinSize = options.particleMinSize;
        if (options.particleMaxSize !== undefined) this.particleMaxSize = options.particleMaxSize;
        if (options.elongate !== undefined) this.elongate = options.elongate;
        if (options.followEntity !== undefined) this.followEntity = options.followEntity;
        // Any option change (or initial construction) requires re-uploading
        // the scalar uniforms; update() will pick this up on the next frame.
        this._scalarsDirty = true;
    }

    /**
     * Notify the instance that one or more scalar fields (speed, drift,
     * opacity, color, particleMin/MaxSize, elongate) were mutated directly
     * — i.e. without going through `applyOptions()`. Without this call the
     * animation handler in createViewer.ts would set the JS field but the
     * GPU uniform would stay frozen, because update() skips re-upload when
     * `_scalarsDirty` is false.
     */
    markScalarsDirty(): void {
        this._scalarsDirty = true;
    }

    /** Drift sideways with the wind: world-space velocity in units / s (0, 0 = still air). */
    setWind(x: number, z: number): void {
        this._windX = Number.isFinite(x) ? x : 0;
        this._windZ = Number.isFinite(z) ? z : 0;
    }

    get cellSize(): number {
        return 1 / Math.max(this.density, 0.1);
    }

    private _halfCells(extent: number): number {
        return Math.min(128, Math.max(1, Math.floor(extent * Math.max(this.density, 0.1))));
    }

    get numParticles(): number {
        const hx = this._halfCells(this.extents.x);
        const hy = this._halfCells(this.extents.y);
        const hz = this._halfCells(this.extents.z);
        return hx * 2 * hy * 2 * hz * 2;
    }

    private _buildFormat() {
        const PC = pc as any;
        this._format = new PC.GSplatFormat(this.app.graphicsDevice, [
            { name: 'data', format: PC.PIXELFORMAT_RGBA8 }
        ], {
            readGLSL,
            readWGSL
        });
    }

    /** Rebuild the particle grid. Call after changing extents or density. */
    rebuild() {
        this._buildContainer();
    }

    private _buildContainer() {
        const PC = pc as any;
        if (this._container) {
            this._container.destroy();
            this._container = null;
        }

        const device = this.app.graphicsDevice;
        let halfX = this._halfCells(this.extents.x);
        let halfY = this._halfCells(this.extents.y);
        let halfZ = this._halfCells(this.extents.z);

        // Enforce the soft particle cap by scaling down half-extents uniformly
        // until the product is under MAX_WEATHER_PARTICLES. This preserves the
        // shape of the volume while keeping memory bounded.
        let maxSplats = (halfX * 2) * (halfY * 2) * (halfZ * 2);
        if (maxSplats > MAX_WEATHER_PARTICLES) {
            const scale = Math.cbrt(MAX_WEATHER_PARTICLES / maxSplats);
            halfX = Math.max(1, Math.floor(halfX * scale));
            halfY = Math.max(1, Math.floor(halfY * scale));
            halfZ = Math.max(1, Math.floor(halfZ * scale));
            const capped = (halfX * 2) * (halfY * 2) * (halfZ * 2);
            console.warn(`[GsplatWeather] Clamped particle count from ${maxSplats} to ${capped} (cap ${MAX_WEATHER_PARTICLES})`);
            maxSplats = capped;
        }

        const gridX = halfX * 2;
        const gridY = halfY * 2;
        const gridZ = halfZ * 2;
        const cs = this.cellSize;

        this._effectiveHalfCells[0] = halfX;
        this._effectiveHalfCells[1] = halfY;
        this._effectiveHalfCells[2] = halfZ;

        this._container = new PC.GSplatContainer(device, maxSplats, this._format);

        const texData = this._container.getTexture('data').lock();
        const centers = this._container.centers;
        let idx = 0;
        for (let x = 0; x < gridX; x++) {
            for (let y = 0; y < gridY; y++) {
                for (let z = 0; z < gridZ; z++) {
                    texData[idx * 4 + 0] = x;
                    texData[idx * 4 + 1] = y;
                    texData[idx * 4 + 2] = z;
                    texData[idx * 4 + 3] = (Math.random() * 256) | 0;

                    centers[idx * 3 + 0] = (x - halfX + 0.5) * cs;
                    centers[idx * 3 + 1] = (y - halfY + 0.5) * cs;
                    centers[idx * 3 + 2] = (z - halfZ + 0.5) * cs;
                    idx++;
                }
            }
        }
        this._container.getTexture('data').unlock();

        const halfExtX = halfX * cs;
        const halfExtY = halfY * cs;
        const halfExtZ = halfZ * cs;
        this._container.aabb = new pc.BoundingBox(pc.Vec3.ZERO, new pc.Vec3(halfExtX, halfExtY, halfExtZ));
        this._container.update(maxSplats, true);

        if (!this.entity.gsplat) {
            this.entity.addComponent('gsplat', {
                resource: this._container,
                unified: true
            });
        } else {
            (this.entity.gsplat as any).resource = this._container;
        }
        // The weather splat animates via a `uTime` uniform read in the getCenter()
        // vertex shader, so its per-camera work buffer must re-bake every frame.
        // WORKBUFFER_UPDATE_ALWAYS is the engine's intended path for time-animated
        // unified splats and is evaluated per-camera-manager — so a second camera
        // (e.g. a mirror's reflection camera at priority -100) can no longer steal
        // the shared renderDirty flag and leave the main view frozen. Also fixes the
        // no-mirror case where a stationary camera froze the snow/rain.
        if ((pc as any).WORKBUFFER_UPDATE_ALWAYS !== undefined) {
            (this.entity.gsplat as any).workBufferUpdate = (pc as any).WORKBUFFER_UPDATE_ALWAYS;
        }
    }

    update(dt: number) {
        if (!this._container || !this.entity.gsplat) return;
        if (!this.entity.enabled) return;

        // Time advances unconditionally so that fading opacity from 0 → 1
        // doesn't snap the particle phase to where it was when fade-out began.
        this._time += dt;

        // Strict zero: any positive opacity should render. The previous 0.001
        // threshold silently swallowed slow fade-in animations.
        if (this.opacity <= 0) return;

        const cs = this.cellSize;
        let camX = 0, camY = 0, camZ = 0;

        if (this.followEntity) {
            const pos = this.followEntity.getPosition();
            camX = pos.x;
            camY = pos.y;
            camZ = pos.z;
        } else {
            const pos = this.entity.getPosition();
            camX = pos.x;
            camY = pos.y;
            camZ = pos.z;
        }

        const bcX = Math.floor(camX / cs);
        const bcY = Math.floor(camY / cs);
        const bcZ = Math.floor(camZ / cs);

        this.entity.setPosition(bcX * cs, bcY * cs, bcZ * cs);

        this._baseCellArray[0] = bcX;
        this._baseCellArray[1] = bcY;
        this._baseCellArray[2] = bcZ;

        this._camPosArray[0] = camX;
        this._camPosArray[1] = camY;
        this._camPosArray[2] = camZ;

        this._gridHalfArray[0] = this._effectiveHalfCells[0];
        this._gridHalfArray[1] = this._effectiveHalfCells[1];
        this._gridHalfArray[2] = this._effectiveHalfCells[2];

        if (this._scalarsDirty) {
            this._particleSizeArray[0] = this.particleMinSize;
            this._particleSizeArray[1] = this.particleMaxSize;
        }

        const gs = this.entity.gsplat as any;
        // Wind drift: accumulated in cells and wrapped to the grid so it never loses precision.
        const drifting = this._windX !== 0 || this._windZ !== 0;
        if (drifting || this._windPushed) {
            const gx = this._effectiveHalfCells[0] * 2;
            const gz = this._effectiveHalfCells[2] * 2;
            const step = Math.min(Math.max(dt, 0), 0.25);
            const wrap = (v: number, n: number) => v - Math.floor(v / n) * n;
            this._windDrift[0] = wrap(this._windDrift[0] + (this._windX * step) / cs, gx);
            this._windDrift[1] = wrap(this._windDrift[1] + (this._windZ * step) / cs, gz);
            this._windDrift[3] = drifting ? 1 : 0;
            // Streaks lean into the wind: tilt +Y toward the drift by atan(wind / fall speed).
            const wind = Math.hypot(this._windX, this._windZ);
            const fall = Math.max(0.05, 0.55 * this.speed * cs);
            const angle = Math.atan2(wind, fall);
            if (wind > 1e-6) {
                // Rotating +Y by +angle about (-wind.z, 0, wind.x) tilts it up-and-upwind: the
                // streak lies along the particles' path (down and downwind).
                const ax = -this._windZ / wind, az = this._windX / wind;
                const sn = Math.sin(angle / 2), cn = Math.cos(angle / 2);
                this._windRot[0] = cn; // w
                this._windRot[1] = ax * sn; // x
                this._windRot[2] = 0; // y
                this._windRot[3] = az * sn; // z
            }
            gs.setParameter('uWindDrift', this._windDrift);
            gs.setParameter('uWindRot', this._windRot);
            this._windPushed = drifting;
        } else if (this._scalarsDirty) {
            gs.setParameter('uWindDrift', this._windDrift);
            gs.setParameter('uWindRot', this._windRot);
        }
        // uTime, uBaseCell, uCameraPos, uCellSize, uGridHalf change every frame
        // (or on camera move) so they're always uploaded.
        gs.setParameter('uTime', this._time);
        gs.setParameter('uCellSize', cs);
        gs.setParameter('uGridHalf', this._gridHalfArray);
        gs.setParameter('uBaseCell', this._baseCellArray);
        gs.setParameter('uCameraPos', this._camPosArray);

        // Scalar uniforms only change when applyOptions() runs. Skipping these
        // in steady state saves ~6 setParameter calls per frame (and a
        // material.update()) per active weather instance.
        if (this._scalarsDirty) {
            gs.setParameter('uSpeed', this.speed);
            gs.setParameter('uDrift', this.drift);
            gs.setParameter('uOpacity', this.opacity);
            gs.setParameter('uColor', this.color);
            gs.setParameter('uParticleSize', this._particleSizeArray);
            gs.setParameter('uElongate', this.elongate);
            this._scalarsDirty = false;
        }

        // Gsplat materials require an explicit update() call for parameter
        // changes to upload to the GPU — setParameter alone is insufficient.
        const material = gs.material;
        if (material && typeof material.update === 'function') {
            material.update();
        }
    }

    destroy() {
        if (this._container) {
            this._container.destroy();
            this._container = null;
        }
        if (this.entity) {
            this.entity.destroy();
        }
    }
}

export type WeatherPreset = 'snow' | 'rain' | 'custom';

export const WEATHER_PRESETS: Record<Exclude<WeatherPreset, 'custom'>, Required<Omit<GsplatWeatherOptions, 'followEntity' | 'extents'>>> = {
    snow: {
        density: 2,
        speed: 1.0,
        drift: 0.15,
        opacity: 0.85,
        color: [1, 1, 1],
        particleMinSize: 0.006,
        particleMaxSize: 0.014,
        elongate: 1.0
    },
    rain: {
        density: 3,
        speed: 18.0,
        drift: 0.02,
        opacity: 0.55,
        color: [0.8, 0.85, 0.95],
        particleMinSize: 0.004,
        particleMaxSize: 0.008,
        elongate: 12.0
    }
};
